import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AdaptiveDashboardProvider, useAdaptiveDashboard, DashboardLayout } from '@/contexts/AdaptiveDashboardContext';
import AdaptiveDashboardLayout from './AdaptiveDashboardLayout';
import { Skeleton } from '@/components/ui/skeleton';
import { LayoutDashboard, TrendingUp, Sparkles } from 'lucide-react';

const DashboardContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const { 
    preferences, 
    toolCompletions, 
    recommendations, 
    isLoading, 
    updateLayout,
    trackEntry 
  } = useAdaptiveDashboard();

  const [currentLayout, setCurrentLayout] = useState<DashboardLayout>('balanced');

  // Determine layout based on entry path and preferences
  useEffect(() => {
    if (preferences) {
      // Use saved preference as default
      setCurrentLayout(preferences.preferred_layout);
    } else if (location.state?.from) {
      // Adapt layout based on entry path
      const entryPath = location.state.from;
      if (entryPath.includes('score') || entryPath.includes('assessment')) {
        setCurrentLayout('score-focused');
      } else if (entryPath.includes('rate') || entryPath.includes('whisperer')) {
        setCurrentLayout('rate-focused');
      } else if (entryPath.includes('artifacts') || entryPath.includes('big-sheet')) {
        setCurrentLayout('journey-focused');
      } else if (entryPath.includes('opportunity') || entryPath.includes('power-moves') || entryPath.includes('okrs')) {
        setCurrentLayout('decision-focused');
      }
    }
  }, [preferences, location.state]);

  const handleLayoutChange = async (newLayout: DashboardLayout) => {
    setCurrentLayout(newLayout);
    await updateLayout(newLayout);
  };

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    const timeGreeting = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
    const userName = session?.user?.email?.split('@')[0] || 'there';
    
    return `Good ${timeGreeting}, ${userName}! Ready to advance your career?`;
  };

  const getLayoutDescription = () => {
    const descriptions = {
      'balanced': 'A comprehensive view of all your career tools and progress.',
      'score-focused': 'Dive deep into your assessment progress and professional development.',
      'rate-focused': 'Focus on rate calculation and financial planning tools.',
      'journey-focused': 'Track your career story and documentation progress.',
      'decision-focused': 'Make strategic career decisions with planning tools.',
    };
    return descriptions[currentLayout];
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <Skeleton className="h-32 w-full" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          </div>
          
          <div className="space-y-6">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Career Command Center
            </h1>
            <p className="text-lg text-muted-foreground mt-1">
              {getWelcomeMessage()}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => navigate('/workspace')}
              className="hidden md:flex"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              AI Assistant
            </Button>
          </div>
        </div>

        {/* Layout Info */}
        <Card className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <LayoutDashboard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-semibold text-sm">
                  {currentLayout.split('-').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')} Layout
                </div>
                <div className="text-sm text-muted-foreground">
                  {getLayoutDescription()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-success/10 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {toolCompletions.filter(c => c.completion_status === 'completed').length}
                  </div>
                  <div className="text-sm text-muted-foreground">Tools Completed</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent-blue/10 rounded-lg">
                  <LayoutDashboard className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {Math.round(
                      toolCompletions.reduce((sum, c) => sum + c.completion_percentage, 0) / 
                      Math.max(toolCompletions.length, 1)
                    )}%
                  </div>
                  <div className="text-sm text-muted-foreground">Avg. Progress</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{recommendations.length}</div>
                  <div className="text-sm text-muted-foreground">Smart Recommendations</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Adaptive Dashboard Layout */}
      <AdaptiveDashboardLayout
        layout={currentLayout}
        userCompletions={toolCompletions}
        recommendations={recommendations}
        onLayoutChange={handleLayoutChange}
      />
    </div>
  );
};

const EnhancedDashboard = () => {
  const session = useSession();

  if (!session) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Sign In Required</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">Please sign in to access your personalized dashboard.</p>
            <Button onClick={() => window.location.href = '/auth'} className="w-full">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AdaptiveDashboardProvider>
      <DashboardContent />
    </AdaptiveDashboardProvider>
  );
};

export default EnhancedDashboard;